import { AddModuleResponse, IGenericError, Invoice, LastConfigurationResponse, ModuleStatusResponse, SubscriptionDiscountResponseDTO, SubscriptionResponse } from '@libs/entity-lib';
import { createReducer, on } from '@ngrx/store';
import * as fromActions from './subscription.actions';

export interface State {
  loading: boolean;
  /**
   * Loading próprio das faturas. NÃO usar o `loading` global aqui: a página
   * subscription esconde o conteúdo das tabs quando `loading && !subscription`,
   * e o fetch de faturas disparado pelo construtor da tab destruiria/recriaria
   * a própria tab em ciclo infinito (CE sem assinatura carregada).
   */
  invoicesLoading: boolean;
  /** null = nunca buscadas nesta sessão (guard do fetch inicial). */
  invoicesLoaded: boolean;
  error: IGenericError | null;
  subscriptions: SubscriptionResponse[];
  invoices: Invoice[];
  discounts: SubscriptionDiscountResponseDTO[];
  allowRestaurant?: boolean;
  modulesStatus: ModuleStatusResponse[];
  modulesStatusLastFetched: number | null;
  addModuleResponse: AddModuleResponse | null;
  /** Última assinatura (reativação). null pode ser "não buscado" ou "nunca assinou" — ver flag abaixo. */
  lastConfiguration: LastConfigurationResponse | null;
  /** 'none' = 404 (nunca assinou). */
  lastConfigurationState: 'idle' | 'loading' | 'loaded' | 'none';
}

export const initialState: State = {
  error: null,
  loading: false,
  invoicesLoading: false,
  invoicesLoaded: false,
  invoices: [],
  discounts: [],
  allowRestaurant: true,
  subscriptions: [],
  modulesStatus: [],
  modulesStatusLastFetched: null,
  addModuleResponse: null,
  lastConfiguration: null,
  lastConfigurationState: 'idle',
};

export const reducer = createReducer(
  initialState,
  on(fromActions.fetchSubscription, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(fromActions.fetchSubscriptionSuccessfuly, (state, { subscriptions }) => ({
    ...state,
    loading: false,
    subscriptions,
  })),
  on(fromActions.fetchSubscriptionFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),


  on(fromActions.fetchRestaurantSubscription, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(fromActions.fetchRestaurantSubscriptionSuccessfuly, (state, { allowRestaurant }) => ({
    ...state,
    loading: false,
    allowRestaurant,
  })),
  on(fromActions.fetchRestaurantSubscriptionFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(fromActions.cancelSubscription, (state) => ({
    ...state,
    loading: true,
    error: null,
    cardSelected: undefined,
  })),
  on(fromActions.cancelSubscriptionSuccessfuly, (state) => ({
    ...state,
    loading: false,
    subscription: undefined,
  })),

  on(fromActions.changeSubscriptionCard, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(fromActions.changeSubscriptionCardSuccessfuly, (state) => ({
    ...state,
    loading: false
  })),
  on(fromActions.changeSubscriptionCardFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(fromActions.fetchLastConfiguration, (state) => ({
    ...state,
    lastConfigurationState: 'loading' as const,
  })),
  on(fromActions.fetchLastConfigurationSuccess, (state, { config }) => ({
    ...state,
    lastConfiguration: config,
    lastConfigurationState: config ? ('loaded' as const) : ('none' as const),
  })),
  on(fromActions.fetchLastConfigurationFailed, (state) => ({
    ...state,
    lastConfigurationState: 'idle' as const,
  })),

  on(fromActions.fetchInvoices, (state) => ({
    ...state,
    invoicesLoading: true,
    error: null,
  })),
  on(fromActions.fetchInvoicesSuccessfuly, (state, { invoices }) => ({
    ...state,
    invoicesLoading: false,
    invoicesLoaded: true,
    invoices,
  })),
  on(fromActions.fetchInvoicesFailed, (state, { error }) => ({
    ...state,
    invoicesLoading: false,
    invoicesLoaded: true,
    error,
  })),

  // Fetch Discounts By Subscription ID
  on(fromActions.fetchSubscriptionDiscounts, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(fromActions.fetchSubscriptionDiscountsSuccess, (state, { discounts }) =>
    ({ ...state, loading: false, discounts })
  ),
  on(fromActions.fetchSubscriptionDiscountsFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Add Module
  on(fromActions.addModule, (state) => ({
    ...state,
    loading: true,
    error: null,
    addModuleResponse: null,
  })),
  on(fromActions.addModuleSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    addModuleResponse: response,
  })),
  on(fromActions.addModuleFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Cancel Module
  on(fromActions.cancelModule, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(fromActions.cancelModuleSuccess, (state) => ({
    ...state,
    loading: false,
  })),
  on(fromActions.cancelModuleFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Modules Status
  on(fromActions.fetchModulesStatus, (state) => ({
    ...state,
  })),
  on(fromActions.fetchModulesStatusSuccess, (state, { modulesStatus }) => ({
    ...state,
    modulesStatus,
    modulesStatusLastFetched: Date.now(),
  })),
  on(fromActions.fetchModulesStatusFailed, (state, { error }) => ({
    ...state,
    error,
  }))
);
